import React, { useContext, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Accordion, Anchor, Text, Title } from "@mantine/core"
import { IconArrowRight } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../stores/AppWorkspace"
import { LANDING_COPY } from "../../docs/catalog"
import { FAQ_HASH } from "../helpers/constants"
import { CopyAnchorButton } from "./CopyAnchorButton"

// The homepage FAQ, above the framework grid: short questions with the answers
// collapsed. Every question is an anchor of its own (/#faq-withdraw) with a
// button that copies its link, and a link to one opens it. The words are
// config/landing_copy.json's "home" block, which StructuredDataHelper also
// publishes as the home page's FAQPage.
const FAQ = LANDING_COPY.home.faq
const ANCHORS = new Set(FAQ.items.map((item) => item.anchor))

export const HomeFaq: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const [open, setOpen] = useState<string[]>([])

  // AppWorkspaceContainer scrolls to the hash; this only opens the answer.
  const hash = workspace.hash
  useEffect(() => {
    if (ANCHORS.has(hash)) setOpen((current) => (current.includes(hash) ? current : [...current, hash]))
  }, [hash])

  return (
    <section id={FAQ_HASH} className="or-home-faq" aria-labelledby="or-home-faq-title">
      <Text className="or-eyebrow">{FAQ.eyebrow}</Text>
      <Title order={2} id="or-home-faq-title" className="or-frameworks-title">
        {FAQ.headline}
      </Title>

      {/* order={3} makes each question an h3 under the section's h2. The copy
          button sits beside the control, not in it: a button in a button is
          invalid, and clicking it must not toggle the answer. */}
      <Accordion
        multiple
        value={open}
        onChange={setOpen}
        order={3}
        className="or-home-faq-list"
        classNames={{
          control: "or-home-faq-question",
          label: "or-home-faq-label",
          content: "or-home-faq-answer"
        }}
      >
        {FAQ.items.map((item) => (
          <Accordion.Item key={item.anchor} value={item.anchor} id={item.anchor} className="or-home-faq-item">
            <div className="or-home-faq-control">
              <Accordion.Control>{item.q}</Accordion.Control>
              <CopyAnchorButton anchor={item.anchor} path="/" label="Copy link to this question" />
            </div>
            <Accordion.Panel>
              {item.a}
              {item.href ? (
                <>
                  {" "}
                  <Anchor
                    href={item.href}
                    onClick={(event) => {
                      if (!item.href?.startsWith("/guides")) return
                      event.preventDefault()
                      workspace.openHref(item.href)
                    }}
                  >
                    {item.label ?? "Read more"} <IconArrowRight size={14} aria-hidden="true" />
                  </Anchor>
                </>
              ) : null}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </section>
  )
})
